import Select from "@common/components/Select";
import Grid, { Grid2Props } from "@mui/material/Grid2";
import Typography from "@common/components/Typography";
import { isSuperAdmin } from "@common/utils/user";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import ConfigSwitch from "@pages/Configuration/Monitoring/common/Switch";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { AccountsVisibility, RolePermissions } from "@services/user/types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useRole from "../hooks/useRole";
import ComponentAccess from "./ComponentAccess";
import ConfirmDialog from "./ConfirmDialog";
import { capabilities } from "./utils/constants";
import { AdminPermissions } from "./utils/types";

interface AdminPortalAreaProps {
  canEdit?: boolean;
}

const accountVisibilities = [
  {
    label: "All Accounts",
    value: "AllAccounts",
  },
  {
    label: "Linked Accounts from Tenant",
    value: "HomeTenantAccounts",
  },
  {
    label: "Home Account only",
    value: "HomeAccountOnly",
  },
];

const configSwitchGridProps: Grid2Props = {
  container: true,
  direction: "row",
  sx: { width: "100%", alignItems: "center", gap: 1 },
};

const AdminPortalArea = (props: AdminPortalAreaProps): React.JSX.Element => {
  const { canEdit } = props;
  const { t } = useTranslation();
  const { role, setRole } = useRole();
  const [user] = useAuthenticatedUser();
  const [open, setOpen] = useState(false);
  const [newVisibilityValue, setNewVisibilityValue] = useState<
    string | undefined
  >();

  const isHomeAccountOnly =
    role?.rolePermissions?.adminAccountsVisibility !==
      AccountsVisibility.HomeTenantAccounts &&
    newVisibilityValue === "HomeAccountOnly";
  const roleHasUsers = Boolean(role?.summary?.totalUsers !== 0) ?? false;

  const changePermissions = (key: keyof RolePermissions, value: any) => {
    setRole((old) => {
      const rolePermissions = old.rolePermissions ?? { admin: true };
      return {
        ...old,
        rolePermissions: { ...rolePermissions, [key]: value },
      };
    });
  };

  const onCapabilitiesChange = (event: SelectChangeEvent<any>) => {
    const capability = capabilities.find((c) => c.label === event.target.value);

    setRole((old) => {
      const rolePermissions = old.rolePermissions ?? { admin: true };
      return {
        ...old,
        rolePermissions: { ...rolePermissions, ...capability?.permissions },
      };
    });
  };
  const onVisibilitiesChange = () => {
    changePermissions(
      "adminAccountsVisibility" as keyof RolePermissions,
      newVisibilityValue
    );
    if (newVisibilityValue !== AccountsVisibility.AllAccounts) {
      changePermissions("globalConfiguration", "NoAccess");
      changePermissions("rolesCapabilities", "NoAccess");
    }
    setOpen(false);
  };

  const capabilitiesValue = useMemo(() => {
    let possibleCapabilities = [...capabilities];

    for (let permission of Object.keys(capabilities[0].permissions)) {
      const key = permission as keyof AdminPermissions;
      const selectedValue = role?.rolePermissions?.[key];
      possibleCapabilities = possibleCapabilities.filter(
        (c) => c.permissions[key] === selectedValue
      );

      if (possibleCapabilities.length === 0) break;
    }

    return possibleCapabilities.length !== 0
      ? possibleCapabilities[0].label
      : "Custom";
  }, [role]);

  const disabled = !canEdit;

  const onCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    changePermissions(event.target.name as keyof RolePermissions, checked);
  };

  const handleOpenDialog = (event: SelectChangeEvent<any>) => {
    if (roleHasUsers && event.target.value === "HomeAccountOnly") {
      setNewVisibilityValue(event.target.value);
      setOpen(true);
    } else if (
      roleHasUsers &&
      role?.rolePermissions?.adminAccountsVisibility ===
        AccountsVisibility.AllAccounts &&
      event.target.value === "HomeTenantAccounts"
    ) {
      setNewVisibilityValue(event.target.value);
      setOpen(true);
    } else {
      changePermissions(
        "adminAccountsVisibility" as keyof RolePermissions,
        event.target.value
      );
      if (event.target.value !== AccountsVisibility.AllAccounts) {
        changePermissions("globalConfiguration", "NoAccess");
        changePermissions("rolesCapabilities", "NoAccess");
      }
    }
  };

  return (
    <Grid container direction="column" sx={{ width: "100%", gap: 2 }}>
      <Grid>
        <Typography
          size="medium"
          sx={{
            fontWeight: "medium",
          }}
        >
          {t("Admin Portal")}
        </Typography>
      </Grid>
      <Grid>
        <Select
          disabled={!canEdit}
          value={capabilitiesValue}
          name="account"
          fullWidth
          onChange={onCapabilitiesChange}
          label={t("Set Administration Capabilities")}
        >
          {capabilities.map((c) => {
            return (
              <MenuItem key={c.label} value={c.label}>
                {t(c.label)}
              </MenuItem>
            );
          })}
        </Select>
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"assignAuthorToUsers"}
          checked={role?.rolePermissions?.assignAuthorToUsers ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can assign author permissions to users")}
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"assignRolesToUsers"}
          checked={role?.rolePermissions?.assignRolesToUsers ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can assign roles to users")}
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"linkAccountsToUsers"}
          checked={role?.rolePermissions?.linkAccountsToUsers ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can link accounts to users")}
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"controlDevices"}
          checked={role?.rolePermissions?.controlDevices ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can control devices")}
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"allowBulkChanges"}
          checked={role?.rolePermissions?.allowBulkChanges ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Allow bulk changes")}
      </Grid>
      <Grid>
        <Select
          disabled={disabled}
          value={role?.rolePermissions?.adminAccountsVisibility}
          name="adminAccountsVisibility"
          fullWidth
          onChange={(e) => handleOpenDialog(e)}
          label={t("Accounts Visibility")}
        >
          {accountVisibilities
            .filter((v) => {
              return v.value == AccountsVisibility.AllAccounts
                ? isSuperAdmin(user)
                : true;
            })
            .map((c) => {
              return (
                <MenuItem key={c.value} value={c.value}>
                  {t(c.label)}
                </MenuItem>
              );
            })}
        </Select>
      </Grid>
      <Grid>
        <ComponentAccess canEdit={canEdit} />
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"assignProducer"}
          checked={role?.rolePermissions?.assignProducer ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can assign producers to meetings")}
      </Grid>
      <Grid {...configSwitchGridProps}>
        <ConfigSwitch
          disabled={disabled}
          name={"produceMeetings"}
          checked={role?.rolePermissions?.produceMeetings ?? false}
          onChange={onCheckboxChange}
          checkedLabel=""
          uncheckedLabel=""
        />
        {t("Can produce meetings")}
      </Grid>
      <ConfirmDialog
        open={open}
        onConfirm={onVisibilitiesChange}
        onClose={() => setOpen(false)}
        HomeAccountOnlyText={isHomeAccountOnly}
      />
    </Grid>
  );
};

export default AdminPortalArea;
